import * as React from 'react';
import { useState } from 'react';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { KlaraDocument, KlaraLogEntry, KlaraProject } from '../../model/klara';
import { useAppTranslation } from '../../services/i18n';
import { ProcessLog } from './components/ProcessLog';
import { StatusHeader } from './components/StatusHeader';
import { ImageLightbox } from './components/ImageLightbox';
import { ScoreBadge } from './components/EvaluationBand';
import { CARD_META, GALLERY_BORDER, LOG_RULE, PANEL_BACK } from './colors';
import { formatSize } from './format';
import { protocolsNewestFirst } from './protocols';

type Props = {
  project: KlaraProject;
  log: KlaraLogEntry[];
  downloadUrl: (document: KlaraDocument) => string;
  archiveUrl: string;
  /** The archive filtered to the gallery's images. */
  imageArchiveUrl: string;
};

/**
 * Image types the design draws as their own card rather than as a gallery
 * tile. Only the cover so far — it is a course asset, not an illustration of a
 * chapter.
 */
const IMAGE_CARD_TYPES = ['intro_image'];

/**
 * Whether a document belongs in the gallery rather than in the file grid.
 *
 * Keyed on the mime type, not on `document_type`. The registered `image` type
 * has never actually arrived: the partner sends chapter illustrations under
 * their generic `document` bucket, with `metadata.section` and a description —
 * so matching the type name alone left the gallery empty and dropped every
 * image into the file grid, titled by its filename.
 */
function isGalleryImage(document: KlaraDocument) {
  if (IMAGE_CARD_TYPES.includes(document.document_type)) return false;

  return (
    document.document_type === 'image' ||
    (document.mime_type ?? '').startsWith('image/')
  );
}

/**
 * Výstupy — what came back.
 *
 * Cards are driven by the documents that actually arrived, not by a fixed list
 * of expected types. An unfamiliar document_type still gets a card labelled
 * from what we were given, so the types the partner has announced but not yet
 * shipped — video, podcast — appear the day they start arriving, with no
 * frontend release.
 */
export function OutputsTab({
  project,
  log,
  downloadUrl,
  archiveUrl,
  imageArchiveUrl,
}: Props) {
  const t = useAppTranslation();
  const documents = project.documents ?? [];

  if (project.status === 'failed') {
    return (
      <Box sx={{ padding: '24px', maxWidth: '760px', margin: '0 auto' }}>
        {/* Our own copy: the partner's message is Czech free text we do not
            control, so it stays in the ledger and in Sentry. */}
        <Alert severity="error" sx={{ marginBottom: '16px' }}>
          <Typography sx={{ fontWeight: 600 }}>
            {t('klara.outputs.failedTitle')}
          </Typography>
          <Typography>{t('klara.outputs.failedBody')}</Typography>
        </Alert>
        <ProcessLog entries={log} active={false} stalled={false} />
      </Box>
    );
  }

  if (project.status !== 'completed') {
    return (
      <Box sx={{ padding: '24px', maxWidth: '760px', margin: '0 auto' }}>
        <StatusHeader
          variant="progress"
          // The document the design puts inside the ring on this screen, where
          // Zadání kurzu has a clipboard.
          icon={<DescriptionOutlinedIcon sx={{ fontSize: '40px' }} />}
          title={t('klara.outputs.generating')}
          body={t('klara.outputs.generatingHint')}
        />
        <ProcessLog entries={log} active={!project.stalled} stalled={project.stalled} />
        <Alert severity="info" sx={{ marginTop: '16px' }}>
          {t('klara.outputs.mailPromise')}
        </Alert>
      </Box>
    );
  }

  const gallery = documents.filter(isGalleryImage);
  // Protocols repeat once per QA iteration. The newest is the one that judges
  // the delivered pack; the earlier ones hang off its card rather than filling
  // the grid with cards that differ only by a number.
  const protocols = protocolsNewestFirst(documents);
  const files = documents.filter(
    (document) =>
      !isGalleryImage(document) &&
      (document.document_type !== 'evaluation_protocol' ||
        document === protocols[0])
  );

  return (
    <Box sx={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <TransferHeader project={project} />

      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'var(--color-white)',
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {/* Green whatever the number: this score judges what was delivered
            after the QA loop, and the partner attaches no verdict to it. */}
        <ScoreBadge score={project.final_score} weak={false} />
        <Typography sx={{ flexGrow: 1, fontSize: '18px', fontWeight: 700 }}>
          {t('klara.outputs.resultTitle')}
        </Typography>
        {protocols[0]?.available && (
          <Button
            variant="contained"
            startIcon={<DescriptionOutlinedIcon />}
            href={downloadUrl(protocols[0])}
            sx={{
              flexShrink: 0,
              height: '40px',
              borderRadius: '8px',
              padding: '0 16px',
              whiteSpace: 'nowrap',
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-black)',
              '&:hover': { backgroundColor: 'var(--color-white)' },
            }}
          >
            {t('klara.evaluation.downloadProtocol')}
          </Button>
        )}
      </Box>

      <SectionHeading
        title={t('klara.outputs.files')}
        action={
          // Streamed server-side, so this is a plain link rather than a fetch.
          <ZipButton href={archiveUrl} label={t('klara.outputs.downloadAll')} />
        }
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '20px',
          marginBottom: '32px',
        }}
      >
        {files.map((document) => (
          <DocumentCard
            key={document.id}
            document={document}
            url={downloadUrl(document)}
            // The pack is what the whole run is for, so the design frames it.
            highlighted={document.document_type === 'knowledge_pack'}
            score={
              document.document_type === 'evaluation_protocol'
                ? project.final_score
                : null
            }
            olderProtocols={
              document.document_type === 'evaluation_protocol'
                ? protocols.slice(1)
                : []
            }
            downloadUrl={downloadUrl}
          />
        ))}
      </Box>

      {gallery.length > 0 && (
        <Gallery
          images={gallery}
          downloadUrl={downloadUrl}
          archiveUrl={imageArchiveUrl}
        />
      )}
    </Box>
  );
}

/** A section heading with its action on the right, as both sections have. */
function SectionHeading({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '17px' }}>{title}</Typography>
      {action}
    </Box>
  );
}

/** The dark pill the design uses for both archive downloads. */
function ZipButton({ href, label }: { href: string; label: string }) {
  return (
    <Button
      variant="contained"
      size="small"
      startIcon={<DownloadIcon />}
      href={href}
      sx={{
        backgroundColor: 'var(--color-black)',
        color: 'var(--color-white)',
        borderRadius: '8px',
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: 'var(--color-black)' },
      }}
    >
      {label}
    </Button>
  );
}

/** How many tiles the design shows before "Zobrazit všechny obrázky". */
const GALLERY_PREVIEW = 6;

/**
 * The chapter illustrations.
 *
 * The tiles carry the real image: `downloadUrl` redirects to a presigned url,
 * so the browser can render it straight into an `img`. The design's own tiles
 * show a placeholder glyph, which is what a mock does when it has no bytes.
 */
function Gallery({
  images,
  downloadUrl,
  archiveUrl,
}: {
  images: KlaraDocument[];
  downloadUrl: (document: KlaraDocument) => string;
  archiveUrl: string;
}) {
  const t = useAppTranslation();
  const [expanded, setExpanded] = useState(false);
  // Index into the whole gallery, not into what the grid currently shows, so
  // the arrows walk every picture.
  const [opened, setOpened] = useState<number | null>(null);
  const shown = expanded ? images : images.slice(0, GALLERY_PREVIEW);
  // The archive can only be filtered by a type the server knows, and "image"
  // is the one it files pictures under. A picture recognised only by its mime
  // type would not be in that zip, so the button waits for a real one.
  const archived = images.some((image) => image.document_type === 'image');

  return (
    <>
      <SectionHeading
        title={t('klara.outputs.gallery', { count: images.length })}
        action={
          archived ? (
            <ZipButton href={archiveUrl} label={t('klara.outputs.downloadAll')} />
          ) : undefined
        }
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: '12px',
        }}
      >
        {shown.map((image, position) => (
          <Box
            key={image.id}
            component="button"
            type="button"
            onClick={() => setOpened(position)}
            title={image.file_name ?? undefined}
            sx={{
              display: 'block',
              textAlign: 'left',
              font: 'inherit',
              cursor: 'pointer',
              backgroundColor: PANEL_BACK,
              border: `2px solid ${GALLERY_BORDER}`,
              borderRadius: '10px',
              padding: '2px',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={downloadUrl(image)}
              alt={image.file_name ?? ''}
              // The original, because there is no thumbnail variant to ask
              // for. Chapter illustrations run 3-20 kB, so this costs little —
              // but a generator that starts sending large ones would need a
              // resized url from the backend.
              loading="lazy"
              sx={{
                display: 'block',
                width: '100%',
                height: '92px',
                objectFit: 'cover',
                backgroundColor: GALLERY_BORDER,
              }}
            />
            <Box sx={{ padding: '10px' }}>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--color-black)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {image.file_name ?? t('klara.outputs.untitledImage')}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: CARD_META }}>
                {[
                  typeof image.metadata.section === 'string'
                    ? t('klara.outputs.section', { section: image.metadata.section })
                    : null,
                  formatSize(image.size),
                ]
                  .filter(Boolean)
                  .join(' • ')}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <ImageLightbox
        images={images}
        index={opened}
        onIndexChange={setOpened}
        onClose={() => setOpened(null)}
        downloadUrl={downloadUrl}
      />

      {images.length > GALLERY_PREVIEW && !expanded && (
        <Button
          fullWidth
          onClick={() => setExpanded(true)}
          sx={{
            marginTop: '12px',
            border: `1px solid ${GALLERY_BORDER}`,
            borderRadius: '8px',
            color: 'var(--color-black-text)',
          }}
        >
          {t('klara.outputs.showAllImages')}
        </Button>
      )}
    </>
  );
}

/**
 * Opens the completed Výstupy, and follows the transfer through.
 *
 * Four states over one component: the job is done, the course is being built,
 * the course is built, the build failed. The rest of the screen — the score
 * band, the cards, the gallery — is the same in all four, which is what the
 * design shows: a failed transfer costs the user nothing they generated.
 */
function TransferHeader({ project }: { project: KlaraProject }) {
  const t = useAppTranslation();

  if (project.transfer_status === 'running') {
    return (
      <StatusHeader
        variant="progress"
        title={t('klara.transfer.runningTitle')}
        body={t('klara.transfer.runningBody')}
      />
    );
  }

  if (project.transfer_status === 'completed') {
    return (
      <StatusHeader
        variant="success"
        title={t('klara.transfer.doneTitle')}
        body={t('klara.transfer.doneBody', {
          name: project.inputs.course_name || t('klara.list.untitled'),
        })}
      />
    );
  }

  // The page-level band that carries this on the other tabs is suppressed here,
  // so this is the only place the failure is stated on Výstupy — a green check
  // under a red alert would have the screen contradicting itself.
  if (project.transfer_status === 'failed') {
    return (
      <StatusHeader
        variant="error"
        title={t('klara.transfer.failedTitle')}
        body={t('klara.transfer.failedBody')}
      />
    );
  }

  return (
    <StatusHeader
      variant="success"
      title={t('klara.outputs.doneTitle')}
      body={t('klara.outputs.doneBody')}
    />
  );
}

/**
 * The glyph on a card, by document type.
 *
 * The design draws custom KWS icons, which its own Code Connect note says a
 * developer has to export from Figma into `assets/svg/`. Until they are in the
 * repo these are the nearest icons from the set the rest of the app uses —
 * substituting a real icon, rather than hand-drawing a wrong one.
 */
const CARD_ICONS: Record<string, React.ElementType> = {
  knowledge_pack: MenuBookOutlinedIcon,
  evaluation_protocol: AssignmentTurnedInOutlinedIcon,
  summary: ArticleOutlinedIcon,
  references: HistoryEduOutlinedIcon,
  test_questions: QuizOutlinedIcon,
  gift_questions: QuizOutlinedIcon,
  video: VideocamOutlinedIcon,
  podcast: VolumeUpOutlinedIcon,
  podcast_audio: VolumeUpOutlinedIcon,
  intro_image: ImageOutlinedIcon,
  image: ImageOutlinedIcon,
};

function DocumentCard({
  document,
  url,
  highlighted,
  score,
  olderProtocols,
  downloadUrl,
}: {
  document: KlaraDocument;
  url: string;
  highlighted?: boolean;
  /** Printed in the meta line, the way the design does on the protocol. */
  score?: number | null;
  olderProtocols?: KlaraDocument[];
  downloadUrl: (document: KlaraDocument) => string;
}) {
  const t = useAppTranslation();
  const Icon = CARD_ICONS[document.document_type] ?? InsertDriveFileOutlinedIcon;
  const accent = highlighted ? 'var(--color-primary)' : LOG_RULE;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        padding: '22px',
        borderRadius: '12px',
        backgroundColor: highlighted ? 'var(--color-primary15)' : PANEL_BACK,
        border: highlighted
          ? '2px solid var(--color-primary)'
          : `2px solid ${PANEL_BACK}`,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: '48px',
          height: '48px',
          borderRadius: '10px',
          backgroundColor: accent,
          color: 'var(--color-white)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: '24px' }} />
      </Box>

      <Typography
        sx={{ fontWeight: 600, fontSize: '14px', textAlign: 'center' }}
      >
        {/* Falls back to the partner's own filename when we have no
            friendly label for this document_type — e.g. an image pushed
            under a generic type reads as "document" otherwise, which is
            meaningless, while the filename they sent us is not. The raw
            type string is a last resort for the rare case neither exists. */}
        {t(`klara.documentTypes.${document.document_type}`, {
          defaultValue:
            document.file_name || document.document_type.replace(/_/g, ' '),
        })}
      </Typography>

      <Typography
        sx={{ fontSize: '12px', color: CARD_META, textAlign: 'center' }}
      >
        {[
          extension(document.file_name),
          score === null || score === undefined
            ? null
            : t('klara.outputs.cardScore', { score }),
          formatSize(document.size),
        ]
          .filter(Boolean)
          .join(' • ')}
      </Typography>

      {document.available ? (
        <Button
          variant="contained"
          size="small"
          fullWidth
          startIcon={<DownloadIcon />}
          href={url}
          sx={{
            borderRadius: '8px',
            backgroundColor: accent,
            color: 'var(--color-white)',
            '&:hover': { backgroundColor: accent },
          }}
        >
          {t('klara.outputs.download')}
        </Button>
      ) : (
        // Listed, but there is nothing to serve: a protocol still hosted by the
        // partner, or bytes that never landed.
        <Chip size="small" label={t('klara.outputs.unavailable')} />
      )}

      {/* One card for the protocols, as the build plan asks: the newest is the
          button, the QA iterations before it are links under it. */}
      {(olderProtocols ?? []).length > 0 && (
        <Box sx={{ width: '100%' }}>
          {(olderProtocols ?? []).map((older, index) => (
            <Button
              key={older.id}
              size="small"
              fullWidth
              disabled={!older.available}
              href={downloadUrl(older)}
              sx={{ fontSize: '11px', color: CARD_META }}
            >
              {t('klara.outputs.olderProtocol', {
                iteration: (olderProtocols ?? []).length - index,
              })}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
}

/** The type line the design opens the meta with — "PDF • …". */
function extension(fileName: string | null) {
  const match = /\.([a-z0-9]+)$/i.exec(fileName ?? '');
  return match ? match[1].toUpperCase() : null;
}

